/**
 * DirTreeCache — In-memory Merkle directory tree cache (§11 v3).
 *
 * Two loading modes:
 *   1. Eager: `load()` — recursively fetches ALL directory objects upfront.
 *      Best for GC, merge, and operations that need full tree traversal.
 *   2. Lazy: `loadLazy()` — only marks cache as ready, fetches dirs on demand.
 *      Best for interactive UIs (readdir, stat) on large workspaces.
 *
 * After loading, provides O(depth) readdir/stat and O(depth) mutations
 * with automatic re-encryption of changed ancestors.
 *
 * For 10K files: ~50-100 directory objects × ~2-50 KiB each = ~1-5 MiB cached.
 * Subsequent syncs: only fetch changed directory objects (compare root hash).
 *
 * Usage:
 *   const cache = new DirTreeCache(rootDirHash, store, deps)
 *   await cache.loadLazy()  // instant — no I/O
 *   const entries = await cache.readdirAsync('/docs')  // fetches on demand
 *   const entry = await cache.statAsync('/docs/readme.md')
 *   const result = await cache.mutate('/docs/new.md', { type: 'upsert', entry })
 */

import type {
    DirEntry,
    DirEntryFile,
    DirEntryDir,
    DirectoryObject,
    DirObjectStore,
    DirTreeCodecDeps,
} from './codec/dirTree'
import {
    decryptDirObject,
    encryptDirObject,
    createDirObject,
} from './codec/dirTree'

// ── Types ─────────────────────────────────────────────────────────

export interface MutationResult {
    /** New root directory hash after mutation */
    newRootHash: string
    /** New/changed directory objects (hash → ciphertext) to store in CAS */
    newObjects: Map<string, Uint8Array>
}

export type DirMutation =
    | { type: 'upsert'; entry: DirEntry }
    | { type: 'delete' }

// ── Cache ─────────────────────────────────────────────────────────

const MAX_DEPTH = 256

export class DirTreeCache {
    /** hash → decrypted DirectoryObject */
    private dirs = new Map<string, DirectoryObject>()
    private _rootDirHash: string | null
    private readonly store: DirObjectStore
    private readonly deps: DirTreeCodecDeps
    private loaded = false

    constructor(rootDirHash: string | null, store: DirObjectStore, deps: DirTreeCodecDeps) {
        this._rootDirHash = rootDirHash
        this.store = store
        this.deps = deps
    }

    /** Current root directory hash. */
    get rootDirHash(): string | null {
        return this._rootDirHash
    }

    /** Whether the cache has been loaded. */
    get isLoaded(): boolean {
        return this.loaded
    }

    /** Number of cached directory objects. */
    get size(): number {
        return this.dirs.size
    }

    // ── Load ──────────────────────────────────────────────────────

    /**
     * Load the full tree eagerly into memory.
     * Recursively fetches and decrypts all directory objects.
     * Best for GC and merge which need full tree traversal.
     * Idempotent — calling multiple times is a no-op.
     */
    async load(): Promise<void> {
        if (this.loaded) return
        this.dirs.clear()
        if (this._rootDirHash) {
            await this.loadDir(this._rootDirHash, 0)
        }
        this.loaded = true
    }

    /**
     * Mark the cache as ready for lazy on-demand loading.
     * No I/O occurs — directory objects are fetched when first accessed
     * via `readdirAsync()` or `statAsync()`.
     * Best for interactive UIs on large workspaces.
     */
    loadLazy(): void {
        if (this.loaded) return
        this.loaded = true
    }

    private async loadDir(hash: string, depth: number): Promise<void> {
        if (depth > MAX_DEPTH) throw new Error('DirTreeCache: max depth exceeded')
        if (this.dirs.has(hash)) return // already loaded (dedup + cyclic protection)

        const ciphertext = await this.store.get(hash)
        if (!ciphertext) throw new Error(`DirTreeCache: directory object not found: ${hash}`)

        const dir = await decryptDirObject(ciphertext, this.deps)
        this.dirs.set(hash, dir)

        // Recursively load all subdirectory objects
        for (const entry of dir.entries) {
            if (entry.kind === 'directory') {
                await this.loadDir((entry as DirEntryDir).dirHash, depth + 1)
            }
        }
    }

    /**
     * Fetch and decrypt a single directory object on demand.
     * Caches the result. Returns null if not found.
     */
    private async fetchDirLazy(hash: string): Promise<DirectoryObject | null> {
        const cached = this.dirs.get(hash)
        if (cached) return cached

        const ciphertext = await this.store.get(hash)
        if (!ciphertext) return null

        const dir = await decryptDirObject(ciphertext, this.deps)
        this.dirs.set(hash, dir)
        return dir
    }

    // ── Read (sync — requires eager load) ────────────────────────

    /**
     * List entries in a directory (sync).
     * Requires `load()` first. Returns null if directory doesn't exist.
     */
    readdir(dirPath: string): DirEntry[] | null {
        this.ensureLoaded()
        const dir = this.resolveDir(dirPath)
        return dir ? [...dir.entries] : null
    }

    /**
     * Look up a single entry by path (sync).
     * Requires `load()` first. Returns null if not found.
     */
    stat(path: string): DirEntry | null {
        this.ensureLoaded()
        if (path === '/' || path === '') return null
        const { parentPath, name } = splitPath(path)
        const parentDir = this.resolveDir(parentPath)
        if (!parentDir) return null
        return parentDir.entries.find(e => e.name === name) ?? null
    }

    private resolveDir(dirPath: string): DirectoryObject | null {
        if (!this._rootDirHash) return null

        const segments = parsePath(dirPath)
        let currentHash = this._rootDirHash
        let currentDir = this.dirs.get(currentHash)
        if (!currentDir) return null

        for (const seg of segments) {
            const entry = currentDir.entries.find(e => e.name === seg)
            if (!entry || entry.kind !== 'directory') return null
            currentHash = (entry as DirEntryDir).dirHash
            currentDir = this.dirs.get(currentHash)
            if (!currentDir) return null
        }

        return currentDir
    }

    // ── Read (async — supports both eager and lazy) ──────────────

    /**
     * List entries in a directory (async).
     * Works with both eager and lazy loading.
     * Fetches directory objects on demand if not yet cached.
     */
    async readdirAsync(dirPath: string): Promise<DirEntry[] | null> {
        this.ensureLoaded()
        const dir = await this.resolveDirAsync(dirPath)
        return dir ? [...dir.entries] : null
    }

    /**
     * Stream directory entries recursively as `{path, entry}` pairs.
     * Yields lazily — only fetches directory objects as they are traversed.
     * Supports both eager and lazy loading modes.
     *
     * @param dirPath Root directory to list from (default: '/')
     * @param recursive Whether to recurse into subdirectories (default: true)
     */
    async *listStream(
        dirPath = '/',
        opts?: { recursive?: boolean },
    ): AsyncGenerator<{ path: string; entry: DirEntry }> {
        this.ensureLoaded()
        const dir = await this.resolveDirAsync(dirPath)
        if (!dir) return

        const recursive = opts?.recursive !== false

        for (const entry of dir.entries) {
            const entryPath = dirPath === '/' ? `/${entry.name}` : `${dirPath}/${entry.name}`
            yield { path: entryPath, entry }

            if (recursive && entry.kind === 'directory') {
                yield* this.listStream(entryPath, opts)
            }
        }
    }

    /**
     * Look up a single entry by path (async).
     * Works with both eager and lazy loading.
     * Fetches directory objects on demand if not yet cached.
     */
    async statAsync(path: string): Promise<DirEntry | null> {
        this.ensureLoaded()
        if (path === '/' || path === '') return null
        const { parentPath, name } = splitPath(path)
        const parentDir = await this.resolveDirAsync(parentPath)
        if (!parentDir) return null
        return parentDir.entries.find(e => e.name === name) ?? null
    }

    /**
     * Resolve a path to a directory object, fetching on demand.
     * "/" returns the root directory.
     */
    private async resolveDirAsync(dirPath: string): Promise<DirectoryObject | null> {
        if (!this._rootDirHash) return null

        const segments = parsePath(dirPath)
        let currentDir = await this.fetchDirLazy(this._rootDirHash)
        if (!currentDir) return null

        for (const seg of segments) {
            const entry = currentDir.entries.find(e => e.name === seg)
            if (!entry || entry.kind !== 'directory') return null
            currentDir = await this.fetchDirLazy((entry as DirEntryDir).dirHash)
            if (!currentDir) return null
        }

        return currentDir
    }

    // ── Mutate ────────────────────────────────────────────────────

    /**
     * Apply a mutation at the given path.
     * Re-encrypts all affected directory objects from target up to root.
     * Updates the internal cache to reflect the new state.
     *
     * Returns the new rootDirHash and changed directory objects.
     */
    async mutate(path: string, mutation: DirMutation): Promise<MutationResult> {
        this.ensureLoaded()
        if (path === '/' || path === '') throw new Error('DirTreeCache: cannot mutate root path')

        const newObjects = new Map<string, Uint8Array>()
        const segments = parsePath(path)
        const name = segments[segments.length - 1]
        const parentSegments = segments.slice(0, -1)

        // Phase 1: Walk down from root, collecting ancestor chain
        // Each element is { hash, dir } — we'll rebuild bottom-up
        const chain: Array<{ hash: string; dir: DirectoryObject }> = []

        if (!this._rootDirHash || !this.dirs.has(this._rootDirHash)) {
            if (mutation.type === 'delete') return { newRootHash: this._rootDirHash!, newObjects }
            // Create empty root
            const emptyRoot = createDirObject([])
            const { ciphertext, hash } = await encryptDirObject(emptyRoot, this.deps)
            newObjects.set(hash, ciphertext)
            this.dirs.set(hash, emptyRoot)
            this._rootDirHash = hash
            chain.push({ hash, dir: emptyRoot })
        } else {
            chain.push({ hash: this._rootDirHash, dir: this.dirs.get(this._rootDirHash)! })
        }

        for (const seg of parentSegments) {
            const currentDir = chain[chain.length - 1].dir
            const entry = currentDir.entries.find(e => e.name === seg)

            if (entry && entry.kind === 'directory') {
                const dirEntry = entry as DirEntryDir
                const dir = this.dirs.get(dirEntry.dirHash)
                if (dir) {
                    chain.push({ hash: dirEntry.dirHash, dir })
                    continue
                }
            }

            // Intermediate directory doesn't exist — create it
            if (mutation.type === 'delete') {
                return { newRootHash: this._rootDirHash!, newObjects }
            }

            const emptyDir = createDirObject([])
            const { ciphertext, hash } = await encryptDirObject(emptyDir, this.deps)
            newObjects.set(hash, ciphertext)
            this.dirs.set(hash, emptyDir)

            // Add the new dir entry to current parent
            const dirEntry: DirEntryDir = {
                name: seg,
                kind: 'directory',
                dirHash: hash,
                mtime: Date.now(),
                ctime: Date.now(),
            }
            const updatedParentEntries = [...currentDir.entries, dirEntry]
            const updatedParent = createDirObject(updatedParentEntries)
            const parentResult = await encryptDirObject(updatedParent, this.deps)
            newObjects.set(parentResult.hash, parentResult.ciphertext)
            this.dirs.set(parentResult.hash, updatedParent)

            // Replace the current chain entry with the updated parent
            chain[chain.length - 1] = { hash: parentResult.hash, dir: updatedParent }

            // Push the new empty dir
            chain.push({ hash, dir: emptyDir })
        }

        // Phase 2: Apply mutation to the target directory
        const targetDir = chain[chain.length - 1].dir
        let newEntries: DirEntry[]

        if (mutation.type === 'upsert') {
            const existingIdx = targetDir.entries.findIndex(e => e.name === name)
            if (existingIdx >= 0) {
                newEntries = [...targetDir.entries]
                newEntries[existingIdx] = mutation.entry
            } else {
                newEntries = [...targetDir.entries, mutation.entry]
            }
        } else {
            newEntries = targetDir.entries.filter(e => e.name !== name)
        }

        // Phase 3: Rebuild from target up to root
        let newDir = createDirObject(newEntries)
        let { ciphertext, hash: newHash } = await encryptDirObject(newDir, this.deps)
        newObjects.set(newHash, ciphertext)
        this.dirs.set(newHash, newDir)

        for (let i = chain.length - 2; i >= 0; i--) {
            const parentDir = chain[i].dir
            const childSegment = segments[i]

            const updatedEntries = parentDir.entries.map(e => {
                if (e.name === childSegment && e.kind === 'directory') {
                    return { ...e, dirHash: newHash } as DirEntryDir
                }
                return e
            })

            newDir = createDirObject(updatedEntries)
            const result = await encryptDirObject(newDir, this.deps)
            newHash = result.hash
            newObjects.set(newHash, result.ciphertext)
            this.dirs.set(newHash, newDir)
        }

        this._rootDirHash = newHash
        return { newRootHash: newHash, newObjects }
    }

    // ── Sync ──────────────────────────────────────────────────────

    /**
     * Update the cache to reflect a new root hash (e.g., after sync).
     * Only fetches and caches directory objects that are new (changed).
     * Unchanged subtrees remain cached.
     */
    async updateRoot(newRootHash: string | null): Promise<void> {
        if (newRootHash === this._rootDirHash) return

        this._rootDirHash = newRootHash
        if (!newRootHash) {
            this.dirs.clear()
            return
        }

        // Incrementally load only new/changed objects
        await this.loadDir(newRootHash, 0)
        // Note: old objects remain in cache (harmless, GC'd by Map lifecycle)
    }

    /**
     * Collect all CAS hashes referenced by the tree.
     * Returns directory object hashes + file content hashes.
     */
    collectAllHashes(): Set<string> {
        this.ensureLoaded()
        const hashes = new Set<string>()
        if (!this._rootDirHash) return hashes

        this.collectHashesRecursive(this._rootDirHash, hashes, 0)
        return hashes
    }

    private collectHashesRecursive(dirHash: string, hashes: Set<string>, depth: number): void {
        if (depth > MAX_DEPTH || hashes.has(dirHash)) return
        hashes.add(dirHash)

        const dir = this.dirs.get(dirHash)
        if (!dir) return

        for (const entry of dir.entries) {
            if (entry.kind === 'file') {
                const file = entry as DirEntryFile
                if (file.content.type === 'inline') {
                    hashes.add(file.content.hash)
                } else {
                    hashes.add(file.content.manifestHash)
                }
            } else if (entry.kind === 'directory') {
                this.collectHashesRecursive((entry as DirEntryDir).dirHash, hashes, depth + 1)
            }
        }
    }

    // ── Helpers ───────────────────────────────────────────────────

    private ensureLoaded(): void {
        if (!this.loaded) throw new Error('DirTreeCache: must call load() before accessing')
    }
}

// ── Path utilities ───────────────────────────────────────────────

/** Parse an absolute path into segments, e.g. "/docs/api/spec.md" → ["docs", "api", "spec.md"] */
function parsePath(path: string): string[] {
    const normalized = path.replace(/^\/+|\/+$/g, '')
    if (normalized === '') return []
    return normalized.split('/')
}

/** Split a path into parent path and final name. */
function splitPath(path: string): { parentPath: string; name: string } {
    const segments = parsePath(path)
    const name = segments[segments.length - 1]
    const parentPath = '/' + segments.slice(0, -1).join('/')
    return { parentPath, name }
}
